import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Badge, Button, Card, Loading, PageHeader, TextArea } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { dateTimeBR } from "../../lib/format";

interface SettingItem {
  key: string;
  group: string;
  description: string;
  secret: boolean;
  default: unknown;
  value: unknown;
  isSet: boolean;
  updatedAt: string | null;
}

const GROUPS: Record<string, string> = {
  sales: "Vendas",
  enrollment: "Matrícula",
  billing: "Cobrança",
  scheduling: "Agenda",
  ai: "Simulação com IA",
  support: "Suporte",
  email: "E-mail",
  auth: "Acesso",
  integrations: "Integrações",
};

export function AdminSettings() {
  const q = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<{ items: SettingItem[] }>("/v1/admin/settings"),
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const groups = [...new Set(q.data.items.map((i) => i.group))];
  return (
    <>
      <PageHeader
        title="Configurações"
        subtitle="Valores do registro tipado. Chaves de integração são só de escrita: o valor nunca volta para a tela."
      />
      {groups.map((g) => (
        <Card key={g} title={GROUPS[g] ?? g}>
          <div className="stack">
            {q.data.items
              .filter((i) => i.group === g)
              .map((i) => (
                <SettingRow key={i.key} item={i} />
              ))}
          </div>
        </Card>
      ))}
    </>
  );
}

function SettingRow({ item }: { item: SettingItem }) {
  const qc = useQueryClient();
  const initial = item.secret
    ? ""
    : JSON.stringify(
        item.value,
        null,
        typeof item.value === "object" && item.value !== null ? 2 : 0,
      );
  const [text, setText] = useState(initial);
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => {
      let value: unknown;
      try {
        value = item.secret ? text : JSON.parse(text);
      } catch {
        value = text;
      }
      return api.put("/v1/admin/settings", { key: item.key, value, reason: reason || undefined });
    },
    onSuccess: () => {
      setReason("");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
  const changed = text !== initial;
  return (
    <div className="card tight">
      <div className="row between">
        <div>
          <code className="small">{item.key}</code>{" "}
          {item.secret ? (
            <Badge color="violet">{item.isSet ? "definido" : "não definido"}</Badge>
          ) : null}
          <div className="small muted">{item.description}</div>
        </div>
        {item.updatedAt ? (
          <span className="small muted">alterado {dateTimeBR(item.updatedAt)}</span>
        ) : null}
      </div>
      <div className="stack" style={{ marginTop: 8 }}>
        <TextArea
          label={item.secret ? "Novo valor" : "Valor (JSON)"}
          rows={typeof item.value === "object" && item.value !== null ? 4 : 1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          hint={!item.secret ? `Padrão: ${JSON.stringify(item.default)}` : undefined}
        />
        {changed ? (
          <div className="row">
            <input
              className="input"
              style={{ flex: 1, minWidth: 180 }}
              placeholder="Motivo da mudança"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button size="small" loading={m.isPending} onClick={() => m.mutate()}>
              Salvar
            </Button>
          </div>
        ) : null}
        {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
      </div>
    </div>
  );
}
